import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ais-ui/ui-core';
import { UseFormReturn } from 'react-hook-form';
import { MiscellaneousField } from '../../reportsTypes';

interface SelectItemProps {
  fieldName: string;
  form: UseFormReturn;
  fieldInfo: MiscellaneousField;
}

export const MiscellaneousSelectItem = ({
  fieldName,
  form,
  fieldInfo,
}: SelectItemProps) => {
  // Check if there are duplicate values in options
  const hasDuplicateValues =
    fieldInfo.options && fieldInfo.options.length > 0
      ? new Set(fieldInfo.options.map((opt) => opt.value)).size !==
        fieldInfo.options.length
      : false;

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        // If there are duplicates, we need to create unique values by appending index
        const handleValueChange = (value: string) => {
          if (hasDuplicateValues) {
            // Extract the original value (remove the index suffix)
            const originalValue = value.replace(/-\d+$/, '');
            field.onChange(originalValue);
          } else {
            field.onChange(value);
          }
        };

        // Find the current value with its index if duplicates exist
        const getCurrentValue = () => {
          if (!hasDuplicateValues || !field.value) return field.value;

          const index = fieldInfo.options?.findIndex(
            (opt) => opt.value === field.value,
          );
          return index !== undefined && index >= 0
            ? `${field.value}-${index}`
            : field.value;
        };

        return (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>
                {fieldInfo.name}: {fieldInfo.required && '*'}
              </FormLabel>
              {fieldInfo.help && (
                <InfoIconTooltip className="mr-2">
                  {fieldInfo.help}
                </InfoIconTooltip>
              )}
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                onValueChange={handleValueChange}
                defaultValue={getCurrentValue()}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={fieldInfo.placeholder} />
                </SelectTrigger>
                <SelectContent>
                  {fieldInfo.options?.map((option, index) => (
                    <SelectItem
                      key={`${option.value}-${index}`}
                      value={
                        hasDuplicateValues
                          ? `${option.value}-${index}`
                          : option.value
                      }
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
